/**
 * Perspective-n-point solver in pure TypeScript.
 *
 * Why this exists: the JS ecosystem has no OpenCV `solvePnP` available in a plain,
 * dependency-light package, and the alternative — reading rotation straight out of
 * MediaPipe's facial transformation matrix — was tried first and rejected. That matrix
 * arrives in MediaPipe's own coordinate convention, so the decomposition has to be
 * reverse-engineered by inspection and cannot be checked without a camera and a human head.
 * The previous implementation did exactly that and shipped an unverified sign convention.
 *
 * Solving PnP ourselves against a canonical model means the whole path is deterministic:
 * project a known rotation, recover it, and assert the error. A test can prove the maths.
 *
 * Method: Levenberg-Marquardt over six parameters (Rodrigues rotation vector plus
 * translation), minimising squared reprojection error, with a numeric Jacobian. Six points
 * give twelve residuals for six unknowns. At ~10 fps the cost is negligible (tens of
 * microseconds) and the accuracy is limited by landmark noise, not by the optimiser.
 */

import {
  MODEL_EYE_TO_CHIN_MM,
  MODEL_POINTS,
  projectPoints,
  rotationMatrixFromVector,
  type CameraIntrinsics,
  type Mat3,
  type Vec2,
  type Vec3,
} from './geometry.js';

export interface PnPSolution {
  rotation: Mat3;
  rotationVector: Vec3;
  translation: Vec3;
  /** Mean per-point reprojection error in pixels. */
  reprojectionErrorPx: number;
  iterations: number;
  converged: boolean;
}

export interface PnPOptions {
  maxIterations?: number;
  /** Stop when the mean reprojection error improves by less than this many pixels. */
  convergenceTolerancePx?: number;
  initialGuess?: { rotationVector: Vec3; translation: Vec3 };
}

const PARAM_COUNT = 6;

/**
 * Estimate rotation and translation from 2D-3D correspondences.
 *
 * `imagePoints` must be in the same order as `objectPoints` (which defaults to the canonical
 * face model). Returns `null` when the inputs are degenerate — too few points, or a
 * configuration the optimiser cannot make sense of. A null here becomes an `unknown`
 * attention state, never a guess.
 */
export function solvePnP(
  imagePoints: readonly Vec2[],
  intrinsics: CameraIntrinsics,
  options: PnPOptions = {},
  objectPoints: readonly Vec3[] = MODEL_POINTS,
): PnPSolution | null {
  if (imagePoints.length !== objectPoints.length || imagePoints.length < 4) return null;
  for (const point of imagePoints) {
    if (!Number.isFinite(point[0]) || !Number.isFinite(point[1])) return null;
  }

  const maxIterations = options.maxIterations ?? 40;
  const tolerance = options.convergenceTolerancePx ?? 1e-4;

  const initial = options.initialGuess ?? initialGuess(imagePoints, intrinsics, objectPoints);
  let params: number[] = [
    initial.rotationVector[0],
    initial.rotationVector[1],
    initial.rotationVector[2],
    initial.translation[0],
    initial.translation[1],
    initial.translation[2],
  ];

  let residuals = computeResiduals(params, imagePoints, intrinsics, objectPoints);
  if (residuals === null) return null;
  let error = meanError(residuals);
  let lambda = 1e-3;
  let iterations = 0;
  let converged = false;

  for (; iterations < maxIterations; iterations += 1) {
    const jacobian = computeJacobian(params, imagePoints, intrinsics, objectPoints);
    if (jacobian === null) break;

    // Normal equations: (JtJ + lambda*diag(JtJ)) delta = -Jt r
    const jtj = multiplyTranspose(jacobian, jacobian);
    const jtr = multiplyTransposeVector(jacobian, residuals);

    let applied = false;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const damped = jtj.map((row, i) =>
        row.map((value, j) => (i === j ? value * (1 + lambda) + lambda : value)),
      );
      const delta = solveLinearSystem(
        damped,
        jtr.map((value) => -value),
      );
      if (delta === null) {
        lambda *= 10;
        continue;
      }

      const candidate = params.map((value, index) => value + (delta[index] as number));
      const candidateResiduals = computeResiduals(
        candidate,
        imagePoints,
        intrinsics,
        objectPoints,
      );
      if (candidateResiduals === null) {
        lambda *= 10;
        continue;
      }
      const candidateError = meanError(candidateResiduals);

      if (candidateError < error) {
        const improvement = error - candidateError;
        params = candidate;
        residuals = candidateResiduals;
        error = candidateError;
        lambda = Math.max(lambda / 10, 1e-12);
        applied = true;
        if (improvement < tolerance) converged = true;
        break;
      }
      lambda *= 10;
    }

    if (!applied || converged) {
      converged = converged || !applied;
      break;
    }
  }

  const rotationVector: Vec3 = [params[0] as number, params[1] as number, params[2] as number];
  const translation: Vec3 = [params[3] as number, params[4] as number, params[5] as number];

  // A solution that places the face behind the camera is geometrically meaningless.
  if (!(translation[2] > 0) || !Number.isFinite(error)) return null;

  return {
    rotation: rotationMatrixFromVector(rotationVector),
    rotationVector,
    translation,
    reprojectionErrorPx: error,
    iterations,
    converged,
  };
}

/**
 * Seed the optimiser from apparent face size and position.
 *
 * Depth comes from the ratio of the model's eye-to-chin span to the measured pixel span, and
 * the lateral offset from where the nose sits relative to the principal point. Starting near
 * the answer is what keeps the solver away from the local minima that a from-zero start can
 * fall into at larger angles.
 */
function initialGuess(
  imagePoints: readonly Vec2[],
  intrinsics: CameraIntrinsics,
  objectPoints: readonly Vec3[],
): { rotationVector: Vec3; translation: Vec3 } {
  const nose = imagePoints[0] as Vec2;
  const chin = imagePoints[1] as Vec2;
  const eyeLeft = imagePoints[2] as Vec2;
  const eyeRight = imagePoints[3] as Vec2;

  const eyeMidY = (eyeLeft[1] + eyeRight[1]) / 2;
  const measuredSpan = Math.abs(chin[1] - eyeMidY);
  const modelSpan = objectPoints === MODEL_POINTS ? MODEL_EYE_TO_CHIN_MM : 500;

  const depth =
    measuredSpan > 1
      ? Math.min(5000, Math.max(200, (intrinsics.focalLength * modelSpan) / measuredSpan))
      : 700;

  return {
    rotationVector: [0, 0, 0],
    translation: [
      ((nose[0] - intrinsics.centreX) * depth) / intrinsics.focalLength,
      ((nose[1] - intrinsics.centreY) * depth) / intrinsics.focalLength,
      depth,
    ],
  };
}

function computeResiduals(
  params: readonly number[],
  imagePoints: readonly Vec2[],
  intrinsics: CameraIntrinsics,
  objectPoints: readonly Vec3[],
): number[] | null {
  const rotation = rotationMatrixFromVector([
    params[0] as number,
    params[1] as number,
    params[2] as number,
  ]);
  const translation: Vec3 = [params[3] as number, params[4] as number, params[5] as number];
  if (!Number.isFinite(translation[2])) return null;

  const projected = projectPoints(objectPoints, rotation, translation, intrinsics);
  const residuals: number[] = [];
  for (let index = 0; index < projected.length; index += 1) {
    const predicted = projected[index] as Vec2;
    const measured = imagePoints[index] as Vec2;
    const dx = predicted[0] - measured[0];
    const dy = predicted[1] - measured[1];
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;
    residuals.push(dx, dy);
  }
  return residuals;
}

/** Central-difference Jacobian. Step sizes are scaled per parameter kind. */
function computeJacobian(
  params: readonly number[],
  imagePoints: readonly Vec2[],
  intrinsics: CameraIntrinsics,
  objectPoints: readonly Vec3[],
): number[][] | null {
  const rows = imagePoints.length * 2;
  const jacobian: number[][] = Array.from({ length: rows }, () => new Array<number>(PARAM_COUNT).fill(0));

  for (let column = 0; column < PARAM_COUNT; column += 1) {
    // Rotation is in radians, translation in millimetres — they need different steps.
    const step = column < 3 ? 1e-5 : 1e-2;
    const forward = params.slice();
    const backward = params.slice();
    forward[column] = (params[column] as number) + step;
    backward[column] = (params[column] as number) - step;

    const forwardResiduals = computeResiduals(forward, imagePoints, intrinsics, objectPoints);
    const backwardResiduals = computeResiduals(backward, imagePoints, intrinsics, objectPoints);
    if (forwardResiduals === null || backwardResiduals === null) return null;

    for (let row = 0; row < rows; row += 1) {
      const derivative =
        ((forwardResiduals[row] as number) - (backwardResiduals[row] as number)) / (2 * step);
      (jacobian[row] as number[])[column] = derivative;
    }
  }
  return jacobian;
}

function multiplyTranspose(a: readonly number[][], b: readonly number[][]): number[][] {
  const columns = PARAM_COUNT;
  const result: number[][] = Array.from({ length: columns }, () => new Array<number>(columns).fill(0));
  for (let i = 0; i < columns; i += 1) {
    for (let j = 0; j < columns; j += 1) {
      let sum = 0;
      for (let row = 0; row < a.length; row += 1) {
        sum += ((a[row] as number[])[i] as number) * ((b[row] as number[])[j] as number);
      }
      (result[i] as number[])[j] = sum;
    }
  }
  return result;
}

function multiplyTransposeVector(a: readonly number[][], v: readonly number[]): number[] {
  const result = new Array<number>(PARAM_COUNT).fill(0);
  for (let i = 0; i < PARAM_COUNT; i += 1) {
    let sum = 0;
    for (let row = 0; row < a.length; row += 1) {
      sum += ((a[row] as number[])[i] as number) * (v[row] as number);
    }
    result[i] = sum;
  }
  return result;
}

/** Gaussian elimination with partial pivoting. Returns null for a singular system. */
function solveLinearSystem(matrix: number[][], vector: number[]): number[] | null {
  const size = vector.length;
  const augmented = matrix.map((row, index) => [...row, vector[index] as number]);

  for (let column = 0; column < size; column += 1) {
    let pivotRow = column;
    let pivotValue = Math.abs((augmented[column] as number[])[column] as number);
    for (let row = column + 1; row < size; row += 1) {
      const candidate = Math.abs((augmented[row] as number[])[column] as number);
      if (candidate > pivotValue) {
        pivotValue = candidate;
        pivotRow = row;
      }
    }
    if (pivotValue < 1e-14) return null;

    if (pivotRow !== column) {
      const temp = augmented[column] as number[];
      augmented[column] = augmented[pivotRow] as number[];
      augmented[pivotRow] = temp;
    }

    const pivot = (augmented[column] as number[])[column] as number;
    for (let row = column + 1; row < size; row += 1) {
      const factor = ((augmented[row] as number[])[column] as number) / pivot;
      if (factor === 0) continue;
      for (let k = column; k <= size; k += 1) {
        (augmented[row] as number[])[k] =
          ((augmented[row] as number[])[k] as number) -
          factor * ((augmented[column] as number[])[k] as number);
      }
    }
  }

  const solution = new Array<number>(size).fill(0);
  for (let row = size - 1; row >= 0; row -= 1) {
    let sum = (augmented[row] as number[])[size] as number;
    for (let column = row + 1; column < size; column += 1) {
      sum -= ((augmented[row] as number[])[column] as number) * (solution[column] as number);
    }
    const pivot = (augmented[row] as number[])[row] as number;
    solution[row] = sum / pivot;
    if (!Number.isFinite(solution[row] as number)) return null;
  }
  return solution;
}

function meanError(residuals: readonly number[]): number {
  const pointCount = residuals.length / 2;
  let total = 0;
  for (let index = 0; index < pointCount; index += 1) {
    total += Math.hypot(residuals[index * 2] as number, residuals[index * 2 + 1] as number);
  }
  return total / pointCount;
}
